/* eslint-disable @altermaind/no-magic-numbers */
/**
 * Converts a color string from HEX, RGB, or RGBA format into an RGBA string,
 * applying the specified opacity.
 *
 * @param receivedColor - A color string in HEX (e.g. "#3498db", "#fff", "#1234"),
 *                        or in RGB/RGBA format (e.g. "rgb(255,0,0)" or "rgba(0,255,0,0.5)").
 * @param opacity - An optional opacity value (default is 1) to be applied in the resulting RGBA string.
 * @returns A string in RGBA format with the specified opacity.
 */
export const convertColorToRgba = (receivedColor: string, opacity = 1): string => {
  // Trim any whitespace from the beginning and end of the string.
  const color = receivedColor.trim();

  // If the color is in HEX format (can be #RGB, #RRGGBB, #RGBA, or #RRGGBBAA)
  if (color.startsWith('#')) {
    let hex = color.slice(1);

    // Handle shorthand formats (#RGB or #RGBA)
    if (hex.length === 3 || hex.length === 4) {
      hex = hex
        .split('')
        .map(char => char + char)
        .join('');
    }

    // Handle HEX without or with alpha channel.
    if (hex.length === 6 || hex.length === 8) {
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      // Use the provided opacity, ignoring any alpha value in the HEX string.
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }
  } else if (color.startsWith('rgb')) {
    // Regex to handle both rgb and rgba formats
    const regex = /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*([\d.]+))?\s*\)$/;
    const match = color.match(regex);

    if (match) {
      const r = parseInt(match[1], 10);
      const g = parseInt(match[2], 10);
      const b = parseInt(match[3], 10);
      // Use the provided opacity instead of any alpha in the input.
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }
  }

  // If the format is not recognized, return the original value.
  return receivedColor;
};
